import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { FileText, Download, AlertCircle, CheckCircle2, Clock } from 'lucide-react';
import { PageHeader } from '@/components/page-header';
import { SensitiveData } from '@/components/sensitive-data';
import { fetchWIPReports } from '@/lib/data-service';
import { formatCurrency, formatDate } from '@/lib/utils';
import type { WIPReport } from '@/types';
import { useDisplayDensity } from '@/hooks/use-display-density';
import { useErrorHandler } from '@/hooks/use-error-handler';

export function WIPReportingPage() {
  const density = useDisplayDensity();
  const [loading, setLoading] = useState(true);
  const [wipReports, setWipReports] = useState<WIPReport[]>([]);
  const { handleApiError } = useErrorHandler({ useToast: true });

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      try {
        const data = await fetchWIPReports('wip-reporting');
        setWipReports(data);
      } catch (error) {
        handleApiError(error, '/api/wip-reports', 'GET');
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [handleApiError]);

  const totalWIP = wipReports.reduce((sum, w) => sum + w.wipAmount, 0);
  const totalCosts = wipReports.reduce((sum, w) => sum + w.costsIncurred, 0);
  const totalBilled = wipReports.reduce((sum, w) => sum + w.amountBilled, 0);
  const readyToBill = wipReports.filter(w => w.readyToBill).length;
  const totalUnderBilling = wipReports.reduce((sum, w) => sum + w.underBilling, 0);
  const totalOverBilling = wipReports.reduce((sum, w) => sum + w.overBilling, 0);

  const getAgingBadge = (days: number) => {
    if (days < 30) return <Badge variant="success">0-30 days</Badge>;
    if (days < 60) return <Badge variant="warning">30-60 days</Badge>;
    if (days < 90) return <Badge variant="warning" accent="orange">60-90 days</Badge>;
    return <Badge variant="destructive">90+ days</Badge>;
  };

  return (
    <div className="space-y-6">
      <PageHeader 
        title="WIP Reporting" 
        description="Work in Progress analysis for month-end close"
        actions={
          <>
            <Button variant="outline">
              <Download className="mr-2 h-4 w-4" />
              Export to Excel
            </Button>
            <Button>
              <FileText className="mr-2 h-4 w-4" />
              Generate Report
            </Button>
          </>
        }
      />

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total WIP</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatCurrency(totalWIP)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Unbilled work value
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Ready to Bill</CardTitle>
            <CheckCircle2 className="h-4 w-4 text-success-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success-600">{readyToBill}</div>
            <p className="text-xs text-muted-foreground mt-1">
              Projects ready for invoicing
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Under Billing</CardTitle>
            <AlertCircle className="h-4 w-4 text-warning-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatCurrency(totalUnderBilling)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Work done, not billed
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Over Billing</CardTitle>
            <AlertCircle className="h-4 w-4 text-error-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <SensitiveData>
                {formatCurrency(totalOverBilling)}
              </SensitiveData>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Billed ahead of work
            </p>
          </CardContent>
        </Card>
      </div>

      {/* WIP Table */}
      <Card>
        <CardHeader>
          <CardTitle>WIP Analysis by Project</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className={density.row}>
                <TableHead className={density.header}>Project</TableHead>
                <TableHead className={density.header}>Client</TableHead>
                <TableHead className={`${density.header} text-right`}>Costs Incurred</TableHead>
                <TableHead className={`${density.header} text-right`}>Profit Earned</TableHead>
                <TableHead className={`${density.header} text-right`}>Amount Billed</TableHead>
                <TableHead className={`${density.header} text-right`}>WIP Amount</TableHead>
                <TableHead className={density.header}>Aging</TableHead>
                <TableHead className={density.header}>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow className={density.row}>
                  <TableCell colSpan={8} className={`${density.cell} text-center text-muted-foreground`}>
                    Loading WIP data...
                  </TableCell>
                </TableRow>
              ) : (
                wipReports.map(wip => (
                  <TableRow key={wip.projectId} className={density.row}>
                    <TableCell className={density.cell}>
                      <div className="font-medium">{wip.projectName}</div>
                    </TableCell>
                    <TableCell className={density.cell}>{wip.clientName}</TableCell>
                    <TableCell className={`${density.cell} text-right`}>
                      <SensitiveData>
                        {formatCurrency(wip.costsIncurred)}
                      </SensitiveData>
                    </TableCell>
                    <TableCell className={`${density.cell} text-right`}>
                      <SensitiveData>
                        {formatCurrency(wip.profitEarned)}
                      </SensitiveData>
                    </TableCell>
                    <TableCell className={`${density.cell} text-right`}>
                      <SensitiveData>
                        {formatCurrency(wip.amountBilled)}
                      </SensitiveData>
                    </TableCell>
                    <TableCell className={`${density.cell} text-right`}>
                      <SensitiveData>
                        <span
                          className={`font-semibold ${
                            wip.wipAmount > 0
                              ? 'text-warning-600'
                              : wip.wipAmount < 0
                              ? 'text-error-600'
                              : 'text-success-600'
                          }`}
                        >
                          {formatCurrency(wip.wipAmount)}
                        </span>
                      </SensitiveData>
                    </TableCell>
                    <TableCell className={density.cell}>{getAgingBadge(wip.agingDays)}</TableCell>
                    <TableCell className={density.cell}>
                      {wip.readyToBill ? (
                        <Badge variant="success">
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                          Ready to Bill
                        </Badge>
                      ) : wip.overBilling > 0 ? (
                        <Badge variant="destructive">Over Billed</Badge>
                      ) : (
                        <Badge variant="secondary">In Progress</Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Insights */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">💡 WIP Insights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="h-2 w-2 rounded-full bg-success-600 mt-2" />
              <div>
                <p className="text-sm font-medium">Ready for Invoicing</p>
                <p className="text-xs text-muted-foreground">
                  {readyToBill} project{readyToBill !== 1 ? 's are' : ' is'} ready to bill
                </p>
              </div>
            </div>
            {totalUnderBilling > 50000 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-warning-600 mt-2" />
                <div>
                  <p className="text-sm font-medium">Under Billing Alert</p>
                  <p className="text-xs text-muted-foreground">
                    <SensitiveData>
                      {formatCurrency(totalUnderBilling)}
                    </SensitiveData>{' '}
                    in unbilled work - consider invoicing
                  </p>
                </div>
              </div>
            )}
            {totalOverBilling > 0 && (
              <div className="flex items-start gap-3">
                <div className="h-2 w-2 rounded-full bg-error-600 mt-2" />
                <div>
                  <p className="text-sm font-medium">Over Billing Risk</p>
                  <p className="text-xs text-muted-foreground">
                    <SensitiveData>
                      {formatCurrency(totalOverBilling)}
                    </SensitiveData>{' '}
                    billed ahead of work completion
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">📋 Month-End Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-success-600" />
                <span className="text-sm">Review all WIP amounts</span>
              </div>
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-5 w-5 text-success-600" />
                <span className="text-sm">Verify costs are approved</span>
              </div>
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-warning-600" />
                <span className="text-sm">Process ready-to-bill projects</span>
              </div>
              <div className="flex items-center gap-3">
                <AlertCircle className="h-5 w-5 text-warning-600" />
                <span className="text-sm">Reconcile with Access Financials</span>
              </div>
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-muted-foreground" />
                <span className="text-sm">Generate journal entries</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
